/* global AbortSignal */
/** react-query хуки «Аналитики МП». retry выключен: 503 «витрина не собрана» и 422 ретраями
 *  не лечатся, ошибка должна быть видна сразу. Площадка `mp` — в каждом ключе: данные Ozon и WB
 *  в кэше не смешиваются. Ручки «только Ozon» (поставки, кампания) — без параметра площадки. */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import * as oz from "@/api/ozonAnalytics";
import type { Grain, OzFilters, OzRecon, SupplyBy, TreeRequest } from "@/api/ozonAnalytics";
import {
  analyticsApi,
  getCogsCoverage,
  getDaily,
  getWbRecon,
  type DailyChannel,
  type Mp,
  type WbRecon,
} from "@/api/mpAnalytics";
import * as plans from "@/api/mpPlans";

const STALE = 5 * 60 * 1000;
const NO_SCOPE: string[] = [];
const ROOT = "mp-analytics";

/** Справочник витрины площадки — и для хука, и для `queryClient.fetchQuery` при смене площадки. */
export function metaQuery(mp: Mp) {
  return {
    queryKey: [ROOT, mp, "meta"],
    queryFn: ({ signal }: { signal: AbortSignal }) => analyticsApi(mp).getMeta(signal),
    staleTime: STALE,
    retry: false,
    // ошибка /meta не перезапрашивается при монтировании новых наблюдателей: иначе вкладки
    // (тренд, дерево — свои useMeta) на монтировании сбрасывают запрос в pending, страница их
    // прячет («Загружаю справочник…»), они размонтируются — и так по кругу, ошибка не видна
    retryOnMount: false,
  } as const;
}

/** `enabled=false` — вкладка «План» (справочник площадки ей не нужен). */
export function useMeta(mp: Mp, enabled = true) {
  return useQuery({ ...metaQuery(mp), enabled });
}

/** `scope` — сводка по фактам сущности (карточка); нет/пусто — вся сводка. `enabled=false` —
 *  не запрашивать, пока не готовы параметры (диапазон витрины у карточки поставки). */
export function useSummary(mp: Mp, f: OzFilters, scope: string[] = NO_SCOPE, enabled = true) {
  return useQuery({
    queryKey: [ROOT, mp, "summary", f, scope],
    queryFn: ({ signal }) =>
      scope.length
        ? analyticsApi(mp).getScopedSummary(f, scope, signal)
        : analyticsApi(mp).getSummary(f, signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}

/** Покрытие продаж себестоимостью (плашка «нет себестоимости»); `enabled` — у площадки есть
 *  себестоимость (`mp.ts` `cogs`) и это не вкладка «План». */
export function useCogsCoverage(
  mp: Mp,
  dateFrom: string,
  dateTo: string,
  legal: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [ROOT, mp, "cogs-coverage", dateFrom, dateTo, legal],
    queryFn: ({ signal }) => getCogsCoverage(mp, dateFrom, dateTo, legal, signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}

/** Матрица «По дням» месяца (вкладка «По дням»). */
export function useDaily(
  mp: Mp,
  month: string,
  legal: string | null,
  channel: DailyChannel,
  enabled = true,
) {
  return useQuery({
    queryKey: [ROOT, mp, "daily", month, legal, channel],
    queryFn: ({ signal }) => getDaily(mp, month, legal, channel, signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}

/** Ряд сравнения тренд не рисует — период сравнения в запрос не шлём (и в ключ не кладём:
 *  смена сравнения не должна перезапрашивать тренд). */
export function useTrend(mp: Mp, f: OzFilters, grain: Grain, scope: string[] = NO_SCOPE) {
  const tf: OzFilters = { ...f, cmp_from: null, cmp_to: null };
  return useQuery({
    queryKey: [ROOT, mp, "trend", tf, grain, scope],
    queryFn: ({ signal }) =>
      scope.length
        ? analyticsApi(mp).getScopedTrend(tf, grain, scope, signal)
        : analyticsApi(mp).getTrend(tf, grain, signal),
    staleTime: STALE,
    retry: false,
  });
}

/** Тренд строки P&L для окна графика: сравнение ОСТАЁТСЯ (график — текущий против сравнения). */
export function useTrendSlice(
  mp: Mp,
  f: OzFilters,
  grain: Grain,
  grp: string,
  item: string | null,
) {
  return useQuery({
    queryKey: [ROOT, mp, "trend-slice", f, grain, grp, item],
    queryFn: ({ signal }) => analyticsApi(mp).getTrendSlice(f, grain, grp, item, signal),
    staleTime: STALE,
    retry: false,
  });
}

/** Выручка корзин для окна графика в % выручки: `/trend` БЕЗ среза (у среза `rev` посчитан по
 *  срезу — у расходной группы 0), сравнение ОСТАЁТСЯ. Ключ свой: у `useTrend` сравнение вырезано. */
export function useTrendRevenue(mp: Mp, f: OzFilters, grain: Grain, enabled: boolean) {
  return useQuery({
    queryKey: [ROOT, mp, "trend-rev", f, grain],
    queryFn: ({ signal }) => analyticsApi(mp).getTrend(f, grain, signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}

export function useSupplies(f: OzFilters, by: SupplyBy) {
  return useQuery({
    queryKey: [ROOT, "ozon", "supplies", f, by],
    queryFn: ({ signal }) => oz.getSupplies(f, by, signal),
    staleTime: STALE,
    retry: false,
  });
}

export function useSupplyDetail(
  orderNumber: string,
  legal: string | null,
  alloc: boolean,
  enabled = true,
) {
  return useQuery({
    queryKey: [ROOT, "ozon", "supply", orderNumber, legal, alloc],
    queryFn: ({ signal }) => oz.getSupplyDetail(orderNumber, legal, alloc, signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}

/** Сверка площадки: у Ozon — с балансом ЛК по статьям, у WB — с шапками отчётов WB. Запрос
 *  идёт только у текущей площадки; результат помечен площадкой — по нему выбирается вид. */
export type MpReconQuery =
  | { mp: "ozon"; q: UseQueryResult<OzRecon> }
  | { mp: "wb"; q: UseQueryResult<WbRecon> };

export function useMpRecon(
  mp: Mp,
  monthFrom: string,
  monthTo: string,
  legal: string | null,
  enabled = true,
): MpReconQuery {
  const ozon = useQuery({
    queryKey: [ROOT, "ozon", "recon", monthFrom, monthTo, legal],
    queryFn: ({ signal }) => oz.getRecon(monthFrom, monthTo, legal, signal),
    staleTime: STALE,
    retry: false,
    enabled: enabled && mp === "ozon",
  });
  const wb = useQuery({
    queryKey: [ROOT, "wb", "recon", monthFrom, monthTo, legal],
    queryFn: ({ signal }) => getWbRecon(monthFrom, monthTo, legal, signal),
    staleTime: STALE,
    retry: false,
    enabled: enabled && mp === "wb",
  });
  return mp === "ozon" ? { mp, q: ozon } : { mp, q: wb };
}

/** Карточка артикула (`/product`): ключ — артикул продавца (`vendor_code`) у обеих площадок. */
export function useProduct(mp: Mp, key: string, legal: string | null) {
  return useQuery({
    queryKey: [ROOT, mp, "product", key, legal],
    queryFn: ({ signal }) => analyticsApi(mp).getProduct(key, legal, signal),
    staleTime: STALE,
    retry: false,
  });
}

/** Одна страница `/tree` (блоки карточки: география, поставки, кампании артикула). */
export function useTreePage(mp: Mp, req: TreeRequest, enabled = true) {
  return useQuery({
    enabled,
    queryKey: [ROOT, mp, "tree-page", req],
    queryFn: ({ signal }) => analyticsApi(mp).postTree(req, signal),
    staleTime: STALE,
    retry: false,
  });
}

/** Карточка рекламной кампании (`/campaign/{id}`): статистика Ozon с периодом сравнения. */
export function useCampaign(campaignId: string, f: OzFilters) {
  return useQuery({
    queryKey: [ROOT, "ozon", "campaign", campaignId, f],
    queryFn: ({ signal }) => oz.getCampaign(campaignId, f, signal),
    staleTime: STALE,
    retry: false,
  });
}

/** Планы (`/api/mp-plans`): ключи под общим корнем — после правки/копирования сбрасываются
 *  план/факт и список планов (`PLAN_KEYS`). */
const PLANS_ROOT = "mp-plans";
export const PLAN_KEYS = {
  planFact: [PLANS_ROOT, "plan-fact"] as const,
  list: [PLANS_ROOT, "list"] as const,
};

/** План/факт месяца YYYY-MM по обеим площадкам. */
export function usePlanFact(month: string) {
  return useQuery({
    queryKey: [...PLAN_KEYS.planFact, month],
    queryFn: ({ signal }) => plans.getPlanFact(month, signal),
    staleTime: STALE,
    retry: false,
  });
}

/** Планы месяца (редактор). */
export function usePlans(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: [...PLAN_KEYS.list, year, month],
    queryFn: ({ signal }) => plans.getPlans(year, month, signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}

/** Юрлица витрин обеих площадок (редактор). */
export function usePlanLegals(enabled = true) {
  return useQuery({
    queryKey: [PLANS_ROOT, "legals"],
    queryFn: ({ signal }) => plans.getPlanLegals(signal),
    staleTime: STALE,
    retry: false,
    enabled,
  });
}
